import { Component, effect, inject, input, signal, untracked } from '@angular/core';
import { map, switchMap }                                       from 'rxjs/operators';

import { HttpService }       from 'src/app/services/http.service';
import { BlastService }      from 'src/app/services/blast.service';
import { FonctionsService }  from 'src/app/services/fonctions-partagees.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { Projet }            from 'src/app/bo/projet';
import { Rotation }          from 'src/app/bo/rotation';
import { DonneesSuiviOperationnelComponent } from 'src/app/components/donnees-suivi-operationnel/donnees-suivi-operationnel.component';
import { LegacyOutletDirective } from '../legacy-outlet.directive';

/** Onglet Load & Haul de l'ancien front (tir-detail, onglet 9) — chargement repris de TirDetailComponent. */
@Component({
  selector:   'app-legacy-blast-load-haul',
  standalone: true,
  imports:    [LegacyOutletDirective],
  template: `
    @if (projet(); as projet) {
      <ng-container
        [appLegacyOutlet]="donneesSuivi"
        [appLegacyOutletInputs]="{ tabDonneesSuivi: rotations(), proprietesAfficheesDonneeSuivi: proprietes, donneesChargees: loaded(), estProjet: false, tirId: blastId(), projet: projet }"
        [appLegacyOutletOutputs]="outputs" />
    }
  `,
  styles: [':host { display: block; width: 100%; }'],
})
export class LegacyBlastLoadHaulComponent {
  readonly blastId = input.required<number>();

  private readonly http             = inject(HttpService);
  private readonly blastService     = inject(BlastService);
  private readonly fonctionsService = inject(FonctionsService);
  private readonly utilService      = inject(UtilisateurService);

  protected readonly donneesSuivi = DonneesSuiviOperationnelComponent;
  protected readonly proprietes   = new Rotation().getProprietesAffichees(false);
  protected readonly outputs      = { rechargerDonnees: () => this.chargerDonneesSuivi() };

  protected readonly projet    = signal<Projet | null>(null);
  protected readonly rotations = signal<Rotation[]>([]);
  protected readonly loaded    = signal(false);

  constructor() {
    effect(() => {
      const blastId = this.blastId();
      untracked(() => this.init(blastId));
    });
  }

  /**
   * Le composant attend l'objet Projet complet : on le résout depuis le tir. Il lit aussi
   * `UtilisateurService.currentTir` (site des shifts) : on le positionne avant l'affichage.
   */
  private init(blastId: number): void {
    this.projet.set(null);
    this.loaded.set(false);
    this.blastService.getBlast(blastId).pipe(
      switchMap(tir => this.http.get<Projet>('Projects/' + tir.projetId).pipe(map(projet => ({ tir, projet })))),
    ).subscribe(({ tir, projet }) => {
      if (blastId !== this.blastId()) return;
      this.utilService.currentTir = tir;
      this.projet.set(projet);
      this.chargerDonneesSuivi();
    });
  }

  /** Reprise de TirDetailComponent.chargerDonneesSuivi. */
  private chargerDonneesSuivi(): void {
    const startLoader = setTimeout(() => this.fonctionsService.loading_screen(true), 300);

    this.http.get<Rotation[]>('Blasts/' + this.blastId() + '/Rotations').subscribe({
      next: rotations => {
        this.rotations.set(rotations);
        this.loaded.set(true);
        clearTimeout(startLoader);
        this.fonctionsService.loading_screen(false);
      },
      error: () => {
        clearTimeout(startLoader);
        this.fonctionsService.loading_screen(false);
      },
    });
  }
}
